import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { KanbanCard } from './KanbanCard';
import clsx from 'clsx';

const columnColors = {
  green: {
    header: 'bg-green-100 dark:bg-green-900/30',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-800 dark:text-green-300',
    count: 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200',
  },
  blue: {
    header: 'bg-blue-100 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-800 dark:text-blue-300',
    count: 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
  },
  yellow: {
    header: 'bg-yellow-100 dark:bg-yellow-900/30',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-800 dark:text-yellow-300',
    count: 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200',
  },
  red: {
    header: 'bg-red-100 dark:bg-red-900/30',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-800 dark:text-red-300',
    count: 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200',
  },
  purple: {
    header: 'bg-purple-100 dark:bg-purple-900/30',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-800 dark:text-purple-300',
    count: 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200',
  },
  cyan: {
    header: 'bg-cyan-100 dark:bg-cyan-900/30',
    border: 'border-cyan-200 dark:border-cyan-800',
    text: 'text-cyan-800 dark:text-cyan-300',
    count: 'bg-cyan-200 dark:bg-cyan-800 text-cyan-800 dark:text-cyan-200',
  },
  orange: {
    header: 'bg-orange-100 dark:bg-orange-900/30',
    border: 'border-orange-200 dark:border-orange-800',
    text: 'text-orange-800 dark:text-orange-300',
    count: 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200',
  },
  zinc: {
    header: 'bg-zinc-100 dark:bg-zinc-800/50',
    border: 'border-zinc-200 dark:border-zinc-700',
    text: 'text-zinc-800 dark:text-zinc-300',
    count: 'bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-200',
  },
};

interface KanbanColumnProps<TItem> {
  id: string;
  title: string;
  color: keyof typeof columnColors;
  items: TItem[];
  getItemId: (item: TItem) => string;
  renderCard: (item: TItem, isDragging?: boolean) => React.ReactNode;
  /** Share the board's width and take its full height (see KanbanBoard). */
  fill?: boolean;
  /** Cards cannot be picked up (see KanbanBoard). */
  readOnly?: boolean;
}

export function KanbanColumn<TItem>({
  id,
  title,
  color,
  items,
  getItemId,
  renderCard,
  fill = false,
  readOnly = false,
}: KanbanColumnProps<TItem>): React.ReactElement {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  const colors = columnColors[color];
  const itemIds = items.map(getItemId);

  return (
    <div
      className={clsx(
        'flex flex-col rounded-lg border bg-stone-50 dark:bg-zinc-900 transition-colors duration-200',
        fill ? 'min-h-0 min-w-0 flex-1' : 'w-72 min-w-[288px]',
        colors.border,
        isOver && 'ring-2 ring-sky-500 ring-opacity-50'
      )}
    >
      {/* Column Header */}
      <div
        className={clsx(
          'flex items-center justify-between px-3 py-2 rounded-t-lg border-b',
          colors.header,
          colors.border
        )}
      >
        <h3 className={clsx('font-semibold text-sm', colors.text)}>{title}</h3>
        <span
          className={clsx(
            'px-2 py-0.5 text-xs font-medium rounded-full',
            colors.count
          )}
        >
          {items.length}
        </span>
      </div>

      {/* Column Content */}
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={clsx(
            'flex-1 p-2 space-y-2 overflow-y-auto',
            // Filling, the board sets the height; a column scrolls only if
            // its own cards outgrow it.
            fill ? 'min-h-0' : 'min-h-[200px] max-h-[calc(100vh-300px)]',
            isOver && 'bg-sky-50 dark:bg-sky-900/10'
          )}
        >
          {items.length === 0 ? (
            <div className="flex items-center justify-center h-20 text-zinc-400 dark:text-zinc-400 text-sm italic">
              No items
            </div>
          ) : (
            items.map((item) => (
              <KanbanCard key={getItemId(item)} id={getItemId(item)} disabled={readOnly}>
                {renderCard(item, false)}
              </KanbanCard>
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
}

export default KanbanColumn;
